/**
 * 1on1 タイムラインの取得・更新フック
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 ペアの取得 / 開設 / 共有相手更新 / タイムライン（無限スクロール）/ セッション更新の TanStack Query フック
 * @graph-connects /one-on-one/pairs [calls] ペア取得・開設 API
 * @graph-connects /one-on-one/pairs/sessions [calls] タイムライン取得・セッション更新 API
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOneOnOnePairRequest,
  OneOnOnePairLookupResponse,
  OneOnOneSessionListResponse,
  UpdateOneOnOneSessionRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { oneOnOneApi } from '../lib/api.js';

/** タイムラインの 1 ページ件数 @graph-connects none */
export const ONE_ON_ONE_PAGE_SIZE = 8;

/** ペアの query key @graph-connects none */
export function oneOnOnePairKeyOf(memberEmail: string): readonly unknown[] {
  return ['one-on-one', 'pair', memberEmail.toLowerCase()];
}

/** タイムラインの query key @graph-connects none */
export function oneOnOneSessionsKeyOf(pairId: string): readonly unknown[] {
  return ['one-on-one', 'sessions', pairId];
}

/**
 * メンバーの active な 1on1 ペアのうち自分が参加しているもの（`pairs`）を取得する。閲覧できる
 * ペアが無ければ空（存在は漏らさない）で、`canOpen` で開設可否も返るので、権限判定はここでは行わず
 * サーバーの応答をそのまま使う（カード側で「タイムライン / 開設ボタン / 非表示」に分岐する）
 * @graph-connects /one-on-one/pairs [calls] ペア取得 API
 */
export function useOneOnOnePair(memberEmail: string) {
  return useQuery<OneOnOnePairLookupResponse>({
    queryKey: oneOnOnePairKeyOf(memberEmail),
    queryFn: () => oneOnOneApi.getPairByMember(memberEmail),
    enabled: memberEmail !== '',
    // 「リーダーが開設した直後に本人がマイページを見る」を素直に成立させる。アプリ既定は
    // staleTime 5 分 + refetchOnWindowFocus:false なので、開設前に開いたタブに戻っても
    // `pairs: []` のキャッシュが残り続けてカードが出ない（本人には開設の通知が無い）
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * タイムライン（新しい順）。`nextBefore` をカーソルに「さらに読む」でページを足す
 * @graph-connects /one-on-one/pairs/sessions [calls] タイムライン取得 API
 */
export function useOneOnOneSessions(pairId: string | null) {
  return useInfiniteQuery<
    OneOnOneSessionListResponse,
    Error,
    OneOnOneSessionListResponse[],
    readonly unknown[],
    string | undefined
  >({
    queryKey: oneOnOneSessionsKeyOf(pairId ?? ''),
    queryFn: ({ pageParam }) =>
      oneOnOneApi.listSessions(pairId ?? '', { limit: ONE_ON_ONE_PAGE_SIZE, before: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextBefore ?? undefined,
    select: (data) => data.pages,
    enabled: pairId !== null,
    staleTime: 15_000,
  });
}

/**
 * ペア開設。成功したらメンバーのペア query を取り直す。
 * 1 メンバーに複数リーダーのペアがありうり、どれを見せるか・並び順はサーバーが決めるので、
 * 手元で組み立てずに取り直す
 * @graph-connects /one-on-one/pairs [calls] ペア開設 API (POST)
 */
export function useCreateOneOnOnePair() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOneOnOnePairRequest) => oneOnOneApi.createPair(body),
    onSuccess: async (pair) => {
      await queryClient.invalidateQueries({ queryKey: oneOnOnePairKeyOf(pair.memberEmail) });
    },
  });
}

/**
 * 共有相手の全置換。成功したらメンバーのペア query を取り直す（共有の変更で見える範囲が変わるため）
 * @graph-connects /one-on-one/pairs/participants [calls] 共有相手更新 API (PATCH)
 */
export function useUpdateOneOnOneParticipants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pairId: string; participants: string[] }) =>
      oneOnOneApi.updateParticipants(input.pairId, { participants: input.participants }),
    onSuccess: async (pair) => {
      await queryClient.invalidateQueries({ queryKey: oneOnOnePairKeyOf(pair.memberEmail) });
    },
  });
}

/**
 * セッション更新（done トグル・要約修正・確認・privateNotes）。成功したらタイムラインを取り直す
 * @graph-connects /one-on-one/pairs/sessions [calls] セッション更新 API (PATCH)
 */
export function useUpdateOneOnOneSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      pairId: string;
      sessionId: string;
      body: UpdateOneOnOneSessionRequest;
    }) => oneOnOneApi.updateSession(input.pairId, input.sessionId, input.body),
    onSuccess: (_session, variables) => {
      void queryClient.invalidateQueries({ queryKey: oneOnOneSessionsKeyOf(variables.pairId) });
    },
  });
}

/**
 * 振り返りを作り直す。生成に時間がかかるので、完了後に一覧を取り直す
 * @graph-connects oneOnOneApi.regenerateLeaderFeedback [calls] 再生成 API
 */
export function useRegenerateLeaderFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pairId: string; sessionId: string }) =>
      oneOnOneApi.regenerateLeaderFeedback(input.pairId, input.sessionId),
    onSuccess: (_session, variables) => {
      void queryClient.invalidateQueries({ queryKey: oneOnOneSessionsKeyOf(variables.pairId) });
    },
  });
}
